export const LARGE_SHEET_WIDTH = 1023;
export const LARGE_SHEET_HEIGHT = 2000;

export const LARGE_SHEET_PREVIEW_SCENARIOS = [
  { id: "short-one", label: "Kort navn · 1 linje", text: "Emma", secondaryText: "" },
  { id: "long-one", label: "Langt navn · 1 linje", text: "Sander Amanda Mårdalen", secondaryText: "" },
  { id: "short-two", label: "Navn og telefon · 2 linjer", text: "Emma Nordli", secondaryText: "900 00 000" },
  { id: "long-two", label: "Lang tekst · 2 linjer", text: "Sander Amanda Mårdalen", secondaryText: "+47 900 00 000" },
];

const DEFAULT_LAYER = {
  id: 0,
  xPct: 50,
  yPct: 50,
  widthPct: 40,
  heightPct: 10,
  maxLines: 2,
  sourceFontSize: 30,
  lineHeight: 1.2,
};

export function normalizeLargeSheetLayer(layer) {
  if (!Array.isArray(layer)) return { ...DEFAULT_LAYER };
  return {
    id: Number(layer[0]) || 0,
    xPct: Number(layer[1]) || DEFAULT_LAYER.xPct,
    yPct: Number(layer[2]) || DEFAULT_LAYER.yPct,
    widthPct: Number(layer[3]) || DEFAULT_LAYER.widthPct,
    heightPct: Number(layer[4]) || DEFAULT_LAYER.heightPct,
    maxLines: Math.max(1, Math.min(3, Math.round(Number(layer[5]) || DEFAULT_LAYER.maxLines))),
    sourceFontSize: Math.max(1, Number(layer[6]) || DEFAULT_LAYER.sourceFontSize),
    lineHeight: Math.max(0.85, Math.min(1.5, Number(layer[7]) || DEFAULT_LAYER.lineHeight)),
    wrapGain: Math.max(1, Number(layer[8]) || 1.45),
  };
}

function partitions(words, lineCount) {
  if (lineCount === 1) return [[words.join(" ")]];
  if (words.length < lineCount) return [];
  const result = [];
  const walk = (start, lines) => {
    if (lines.length === lineCount - 1) {
      result.push([...lines, words.slice(start).join(" ")]);
      return;
    }
    const remainingLines = lineCount - lines.length - 1;
    for (let end = start + 1; end <= words.length - remainingLines; end += 1) {
      walk(end, [...lines, words.slice(start, end).join(" ")]);
    }
  };
  walk(0, []);
  return result;
}

function lineCandidates(text, secondaryText, maxLines) {
  const explicit = [text, secondaryText]
    .flatMap((value) => String(value || "").split(/\r?\n/))
    .map((value) => value.trim())
    .filter(Boolean)
    .slice(0, maxLines);
  const hasExplicitBreak = /\r?\n/.test(String(text || "")) || Boolean(String(secondaryText || "").trim());
  if (hasExplicitBreak || explicit.length !== 1) return explicit.length ? [explicit] : [["Navnet ditt"]];
  const words = explicit[0].split(/\s+/).filter(Boolean);
  const candidates = [[explicit[0]]];
  for (let count = 2; count <= Math.min(maxLines, words.length); count += 1) candidates.push(...partitions(words, count));
  return candidates;
}

function fittedSize(lines, box, layer, measureWidth, safety = 0.9) {
  const widthLimit = box.width * safety;
  const heightLimit = box.height * safety;
  const widthSize = Math.min(...lines.map((line) => {
    const onePixelWidth = Math.max(0.01, Number(measureWidth(line, 1)) || 0.01);
    return widthLimit / onePixelWidth;
  }));
  const heightSize = heightLimit / (lines.length * layer.lineHeight);
  // Zepto stores this value in points and scales it against a 500 px work
  // canvas. On the 2000 px production canvas, 30 becomes a 120 px ceiling.
  const sourceCeiling = layer.sourceFontSize * 4 * (box.sheetHeight / LARGE_SHEET_HEIGHT);
  return Math.max(1, Math.min(widthSize, heightSize, sourceCeiling));
}

export function estimateLargeSheetTextWidth(text, fontSize) {
  let units = 0;
  for (const character of String(text || "")) {
    if (/\s/.test(character)) units += 0.3;
    else if (/[ilI1|.,'`]/.test(character)) units += 0.28;
    else if (/[mwMWÆØ]/.test(character)) units += 0.82;
    else if (/[A-ZÅØÆ]/.test(character)) units += 0.64;
    else units += 0.54;
  }
  return units * fontSize;
}

export function resolveLargeSheetTextLayout({
  layer,
  text,
  secondaryText = "",
  sheetWidth = LARGE_SHEET_WIDTH,
  sheetHeight = LARGE_SHEET_HEIGHT,
  textScale = 1,
  measureWidth = estimateLargeSheetTextWidth,
}) {
  const normalized = normalizeLargeSheetLayer(layer);
  const box = {
    x: sheetWidth * normalized.xPct / 100,
    // Zepto stores y as the top of the text box (x is its horizontal centre).
    // The renderers work from a centre point, so add half the configured height.
    y: sheetHeight * normalized.yPct / 100 + sheetHeight * normalized.heightPct / 200,
    width: sheetWidth * normalized.widthPct / 100,
    height: sheetHeight * normalized.heightPct / 100,
    sheetHeight,
  };
  const candidates = lineCandidates(text || "Navnet ditt", secondaryText, normalized.maxLines);
  const scored = candidates.map((lines) => ({ lines, size: fittedSize(lines, box, normalized, measureWidth) }));
  const single = scored.find((candidate) => candidate.lines.length === 1);
  let selected = scored[0];
  for (const candidate of scored.slice(1)) {
    if (candidate.size > selected.size) selected = candidate;
  }
  if (single && selected.lines.length > 1 && selected.size < single.size * normalized.wrapGain) selected = single;

  // The four shallow strips contain decorative artwork close to both edges.
  // Two explicit lines therefore need a smaller optical safe area than the
  // larger labels.  Keeping this rule in the shared layout engine makes the
  // storefront preview, cart image and production PDF agree pixel-for-pixel.
  const isShallowMultiline = selected.lines.length > 1 && normalized.heightPct <= 7.5;
  const contentSafety = isShallowMultiline ? 0.7 : 0.98;
  const hardFit = fittedSize(selected.lines, box, normalized, measureWidth, contentSafety);
  const requestedScale = Math.max(0.7, Math.min(1.25, Number(textScale) || 1));
  const fontSize = Math.min(hardFit, selected.size * requestedScale);
  const lineAdvance = fontSize * normalized.lineHeight;
  return {
    ...box,
    layerId: normalized.id,
    lines: selected.lines,
    fontSize,
    lineAdvance,
    totalHeight: lineAdvance * selected.lines.length,
    contentSafety,
  };
}

function issue(code, message, layerSet = "", layerId = null) {
  return { code, message, layerSet, layerId };
}

export function validateLargeSheetLayerSet(rawLayers, layerSet = "") {
  if (!Array.isArray(rawLayers) || rawLayers.length === 0) {
    return [issue("empty_layer_set", "Oppsettet må ha minst ett tekstfelt.", layerSet)];
  }

  const issues = [];
  const layers = rawLayers.map(normalizeLargeSheetLayer);
  const ids = new Set();
  for (const layer of layers) {
    if (ids.has(layer.id)) issues.push(issue("duplicate_layer_id", `Tekstfelt ${layer.id} finnes flere ganger.`, layerSet, layer.id));
    ids.add(layer.id);
    if (layer.widthPct < 3 || layer.heightPct < 1.5) issues.push(issue("field_too_small", `Tekstfelt ${layer.id} er for lite.`, layerSet, layer.id));
    if (layer.xPct - layer.widthPct / 2 < 0 || layer.xPct + layer.widthPct / 2 > 100 || layer.yPct < 0 || layer.yPct + layer.heightPct > 100) {
      issues.push(issue("outside_sheet", `Tekstfelt ${layer.id} går utenfor produksjonsarket.`, layerSet, layer.id));
    }
    const smallestScenario = Math.min(...LARGE_SHEET_PREVIEW_SCENARIOS.map((scenario) => resolveLargeSheetTextLayout({
      layer: rawLayers[layers.indexOf(layer)],
      text: scenario.text,
      secondaryText: scenario.secondaryText,
    }).fontSize));
    if (smallestScenario < 18) issues.push(issue("text_too_small", `Tekstfelt ${layer.id} gir uleselig liten tekst i lang testvariant.`, layerSet, layer.id));
  }

  for (let leftIndex = 0; leftIndex < layers.length; leftIndex += 1) {
    const left = layers[leftIndex];
    for (let rightIndex = leftIndex + 1; rightIndex < layers.length; rightIndex += 1) {
      const right = layers[rightIndex];
      const overlapX = Math.min(left.xPct + left.widthPct / 2, right.xPct + right.widthPct / 2)
        - Math.max(left.xPct - left.widthPct / 2, right.xPct - right.widthPct / 2);
      const overlapY = Math.min(left.yPct + left.heightPct, right.yPct + right.heightPct)
        - Math.max(left.yPct, right.yPct);
      if (overlapX > 0.15 && overlapY > 0.15) {
        issues.push(issue("overlapping_fields", `Tekstfelt ${left.id} og ${right.id} overlapper.`, layerSet));
      }
    }
  }
  return issues;
}

export function validateLargeSheetTemplate(template) {
  if (template?.outputLayout !== "zepto-large-sheet") return [];
  const layerSets = template.sheetTextLayers || {};
  const issues = Object.entries(layerSets).flatMap(([layerSet, layers]) => validateLargeSheetLayerSet(layers, layerSet));
  const themeField = (template.fields || []).find((field) => field.role === "labelTheme");
  const themeValues = new Set();
  for (const option of themeField?.options || []) {
    if (!option.value || themeValues.has(option.value)) issues.push(issue("duplicate_theme_value", `Motivet ${option.label || "uten navn"} har en ugyldig eller duplisert ID.`));
    themeValues.add(option.value);
    if (!String(option.label || "").trim()) issues.push(issue("missing_theme_label", "Et motiv mangler navn."));
    if (!option.thumbnailUrl && !option.imageUrl) issues.push(issue("missing_theme_preview", `Motivet ${option.label || option.value} mangler forhåndsvisningsbilde.`));
    if (!option.sheetImageUrl) issues.push(issue("missing_theme_sheet", `Motivet ${option.label || option.value} mangler produksjonsark.`));
    if (!option.sheetLayerSet || !layerSets[option.sheetLayerSet]) {
      issues.push(issue("missing_theme_layout", `Motivet ${option.label || option.value} mangler et gyldig designoppsett.`, option.sheetLayerSet || ""));
    }
    if (option.uploaded === true && (Number(option.sheetWidth) !== LARGE_SHEET_WIDTH || Number(option.sheetHeight) !== LARGE_SHEET_HEIGHT)) {
      issues.push(issue("invalid_theme_sheet_size", `Produksjonsarket for ${option.label || option.value} må være ${LARGE_SHEET_WIDTH} × ${LARGE_SHEET_HEIGHT} px.`));
    }
  }
  return issues;
}
